import { colors } from "@eisox/tokens";
import {
  AirQualityThreshold,
  DATA_SEUILS,
  getAirQualityThreshold,
  getAirQualityV2Threshold,
  getHumidityThreshold,
  getLightThreshold,
  getOpenningThreshold,
  getTemperatureThreshold,
  HumidityThreshold,
  LightThreshold,
  OpenningThreshold,
  TemperatureThreshold,
} from "@eisox/valves";

import { getBrightness, getColorFromBrightness } from "~/helpers/color.helper";

/* TEMPERATURE */
export const temperatureGradient = new Map<number, string>([
  [DATA_SEUILS.temperature.MIN - 5, colors.temperature.cold],
  [DATA_SEUILS.temperature.MIN, colors.temperature.cold],
  [DATA_SEUILS.temperature.MIN + 1, colors.temperature.medium],
  [DATA_SEUILS.temperature.MAX - 2, colors.temperature.medium],
  [DATA_SEUILS.temperature.MAX, colors.temperature.hot],
  [DATA_SEUILS.temperature.MAX + 3, colors.temperature.hot],
]);

const temperatureColor: Record<TemperatureThreshold, ValueOf<typeof colors.temperature>> = {
  [TemperatureThreshold.COLD]: colors.temperature.cold,
  [TemperatureThreshold.MEDIUM]: colors.temperature.medium,
  [TemperatureThreshold.HOT]: colors.temperature.hot,
};

export const getTemperatureColor = (temperature: number) => {
  const temperatureThresholds = getTemperatureThreshold(temperature);
  const background = temperatureColor[temperatureThresholds];
  const brightness = getBrightness(background);
  const color = getColorFromBrightness(brightness);

  return { background, color };
};

/* HUMIDITY */
export const humidityGradient = new Map<number, string>([
  [Math.max(DATA_SEUILS.humidity.MIN - 40, 0), colors.humidity.dry],
  [50, colors.humidity.good],
  [Math.min(DATA_SEUILS.humidity.MAX + 40, 100), colors.humidity.good],
]);

const humidityColor: Record<HumidityThreshold, ValueOf<typeof colors.humidity>> = {
  [HumidityThreshold.DRY]: colors.humidity.dry,
  [HumidityThreshold.GOOD]: colors.humidity.good,
  [HumidityThreshold.WET]: colors.humidity.wet,
};

export const getHumidityColor = (humidity: number) => {
  const humidityThresholds = getHumidityThreshold(humidity);
  const background = humidityColor[humidityThresholds];
  const brightness = getBrightness(background);
  const color = getColorFromBrightness(brightness);
  return { background, color };
};

/* AIR QUALITY */
export const airQualityGradient = new Map<number, string>([
  [DATA_SEUILS.airQuality.MAX, colors.airQuality.veryBad],
  [DATA_SEUILS.airQuality.STEP_3 + 50, colors.airQuality.bad],
  [DATA_SEUILS.airQuality.STEP_2 + 60, colors.airQuality.worse],
  [DATA_SEUILS.airQuality.STEP_2 + 10, colors.airQuality.average],
  [DATA_SEUILS.airQuality.STEP_1 - 30, colors.airQuality.good],
  [DATA_SEUILS.airQuality.MIN, colors.airQuality.perfect],
]);

export const airQualityGradientV2 = new Map<number, string>([
  [DATA_SEUILS.airQualityV2.MAX, colors.airQuality.veryBad],
  [DATA_SEUILS.airQualityV2.MAX - 0.5, colors.airQuality.bad],
  [DATA_SEUILS.airQualityV2.STEP_2 - 0.5, colors.airQuality.average],
  [DATA_SEUILS.airQualityV2.STEP_1 - 0.5, colors.airQuality.good],
  [DATA_SEUILS.airQualityV2.MIN, colors.airQuality.perfect],
]);

const airQualityColor: Record<AirQualityThreshold, ValueOf<typeof colors.airQuality>> = {
  [AirQualityThreshold.PERFECT]: colors.airQuality.perfect,
  [AirQualityThreshold.GOOD]: colors.airQuality.good,
  [AirQualityThreshold.AVERAGE]: colors.airQuality.average,
  [AirQualityThreshold.WORSE]: colors.airQuality.worse,
  [AirQualityThreshold.BAD]: colors.airQuality.bad,
  [AirQualityThreshold.VERY_BAD]: colors.airQuality.veryBad,
};

export const getAirQualityColor = (value: number, airQualityV2: boolean) => {
  const airQualityThresholds = airQualityV2 ? getAirQualityV2Threshold(value) : getAirQualityThreshold(value);
  const background = airQualityColor[airQualityThresholds];
  const brightness = getBrightness(background);
  const color = getColorFromBrightness(brightness);
  return { background, color };
};

/* LIGHT */
export const lightGradient = new Map<number, string>([
  [Math.max(DATA_SEUILS.light.MIN - 200, 0), colors.light.dark],
  [DATA_SEUILS.light.MIN + 28, colors.light.dark],
  [DATA_SEUILS.light.MIN + 85, colors.light.medium],
  [DATA_SEUILS.light.MAX - 85, colors.light.medium],
  [DATA_SEUILS.light.MAX - 28, colors.light.bright],
  [DATA_SEUILS.light.MAX + 200, colors.light.bright],
]);

const lightColor: Record<LightThreshold, ValueOf<typeof colors.light>> = {
  [LightThreshold.DARK]: colors.light.dark,
  [LightThreshold.MEDIUM]: colors.light.medium,
  [LightThreshold.BRIGHT]: colors.light.bright,
};

export const getLightColor = (value: number) => {
  const lightThresholds = getLightThreshold(value);
  const background = lightColor[lightThresholds];
  const brightness = getBrightness(background);
  const color = getColorFromBrightness(brightness);
  return { background, color };
};

/* OPENING */
export const openingColor: Record<OpenningThreshold, ValueOf<typeof colors.openning>> = {
  [OpenningThreshold.CLOSED]: colors.openning.closed,
  [OpenningThreshold.HALF]: colors.openning.half,
  [OpenningThreshold.TOTALLY]: colors.openning.totally,
};

export const getOpenningColor = (value: number) => {
  const openingThresholds = getOpenningThreshold(value);
  const background = openingColor[openingThresholds];
  const brightness = getBrightness(background);
  const color = getColorFromBrightness(brightness);
  return { background, color };
};
